import React, { useState, useEffect } from 'react';
import RTModal from '../common/RTModal';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiPost, apiGet } from '../../api/apiClient';

const UpdateAllocationStatusModal = ({ 
  isOpen, 
  mode, 
  allocation, 
  onSuccess,
  onCancel 
}) => {
  const [loading, setLoading] = useState(false);
  const [createAdvancePayment, setCreateAdvancePayment] = useState(true); // Default checked for start mode
  const [endAllocationCheck, setEndAllocationCheck] = useState(null);
  const [checkingEndAllocation, setCheckingEndAllocation] = useState(false);

  // Fetch end allocation check when modal opens in end mode
  useEffect(() => {
    if (isOpen && mode === 'end' && allocation?.allocation_id) {
      fetchEndAllocationCheck();
    }
  }, [isOpen, mode, allocation?.allocation_id]);

  const fetchEndAllocationCheck = async () => {
    try {
      setCheckingEndAllocation(true);
      const response = await apiGet(`/allocation/end_allocation_check/${allocation.allocation_id}`);
      console.log('End allocation check response:', response);
      // The actual data is in response.data
      const data = response.data || response;
      console.log('Reasons:', data.reasons);
      console.log('Corrective actions:', data.corrective_actions);
      setEndAllocationCheck(data);
    } catch (error) {
      console.error('Error checking end allocation status:', error);
      setEndAllocationCheck({
        can_end: false,
        reasons: ['Failed to validate allocation status'],
        corrective_actions: ['Please try again or contact support']
      });
    } finally {
      setCheckingEndAllocation(false);
    }
  };

  if (!isOpen) return null;

  const getModalConfig = () => {
    switch (mode) {
      case 'start':
        return {
          title: 'Start Allocation',
          message: `Confirm that ${allocation?.pg_name} has started the allocation? A user account will be created for the paying guest and an invoice for the advance payment will be generated.`,
          confirmText: 'Start Allocation',
          confirmVariant: 'primary',
          icon: '▶️'
        };
      case 'cancel':
        return {
          title: 'Cancel Allocation',
          message: `Are you sure you want to cancel the allocation for ${allocation?.pg_name}? This will mark the bed as vacant and remove the PG from the bed.`,
          confirmText: 'Cancel Allocation',
          confirmVariant: 'primary',
          icon: '❌'
        };
      case 'end':
        return {
          title: 'End Allocation',
          message: `Confirm that ${allocation?.pg_name}'s allocation has ended? This will mark the bed as vacant and the allocation as completed.`,
          confirmText: 'End Allocation',
          confirmVariant: 'primary',
          icon: '🏁'
        };
      default:
        return {
          title: 'Update Status',
          message: 'Are you sure you want to update this allocation status?',
          confirmText: 'Confirm',
          confirmVariant: 'primary',
          icon: 'ℹ️'
        };
    }
  };

  const config = getModalConfig();

  const handleConfirm = async () => {
    if (!allocation) return;
    
    let newStatus;
    let url;
    switch (mode) {
      case 'start':
        newStatus = 'Allocated';
        // Add createAdvancePayment flag when starting allocation
        url = `/allocation/status/${allocation.allocation_id}/update?status=${newStatus}&createAdvancePayment=${createAdvancePayment}`;
        break;
      case 'cancel':
      case 'end':
        newStatus = 'AllocationEnded';
        url = `/allocation/status/${allocation.allocation_id}/update?status=${newStatus}`;
        break;
      default:
        return;
    }

    try {
      setLoading(true);
      await apiPost(url);
      
      // Call success callback to refresh data and close modal
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error updating allocation status:', error);
      // You could show an error toast here if needed
      alert('Failed to update allocation status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
      <RTButton
        variant="secondary"
        onClick={onCancel}
        disabled={loading}
      >
        Cancel
      </RTButton>
      <RTButton
        variant={config.confirmVariant}
        onClick={handleConfirm}
        disabled={loading || (mode === 'end' && (!endAllocationCheck || !endAllocationCheck.can_end))}
      >
        {loading ? 'Updating...' : config.confirmText}
      </RTButton>
    </div>
  );

  return (
    <RTModal
      isOpen={isOpen}
      onClose={onCancel}
      title={`${config.icon} ${config.title}`}
      footer={footer}
      size="medium"
    >
      {loading && (
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <RTSpinner message="Updating allocation status..." />
        </div>
      )}
      
      {checkingEndAllocation && mode === 'end' && (
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <RTSpinner message="Validating allocation status..." />
        </div>
      )}
      
      {/* End Allocation Validation Messages */}
      {mode === 'end' && endAllocationCheck && !endAllocationCheck.can_end && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#856404', display: 'flex', alignItems: 'center', gap: '8px' }}>
            ⚠️ Cannot End Allocation
          </h4>
          
          {endAllocationCheck.reasons && endAllocationCheck.reasons.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <strong style={{ color: '#856404', display: 'block', marginBottom: '6px' }}>Issues:</strong>
              <ul style={{ margin: '0', paddingLeft: '20px', color: '#856404' }}>
                {endAllocationCheck.reasons.map((reason, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
          
          {endAllocationCheck.corrective_actions && endAllocationCheck.corrective_actions.length > 0 && (
            <div>
              <strong style={{ color: '#856404', display: 'block', marginBottom: '6px' }}>Required Actions:</strong>
              <ul style={{ margin: '0', paddingLeft: '20px', color: '#856404' }}>
                {endAllocationCheck.corrective_actions.map((action, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>{action}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      {mode === 'end' && endAllocationCheck && endAllocationCheck.can_end && (
        <div style={{
          backgroundColor: '#d4edda',
          border: '2px solid #28a745',
          borderRadius: '6px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '20px' }}>✓</span>
          <span style={{ color: '#155724', fontWeight: '500' }}>
            All payments settled. Ready to end allocation.
          </span>
        </div>
      )}
      
      <div style={{ marginBottom: '20px' }}>
        <p style={{ marginBottom: '16px', lineHeight: '1.5' }}>
          {config.message}
        </p>
        
        {allocation && (
          <div style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '6px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#153C7F' }}>Allocation Details</h4>
            <div style={{ display: 'grid', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <strong style={{ color: '#153C7F', minWidth: '100px' }}>PG Name:</strong>
                <span>{allocation.pg_name}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <strong style={{ color: '#153C7F', minWidth: '100px' }}>Building:</strong>
                <span>{allocation.building_name}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <strong style={{ color: '#153C7F', minWidth: '100px' }}>Room:</strong>
                <span>{allocation.room_type}</span>
              </div>
              {allocation.start_date && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <strong style={{ color: '#153C7F', minWidth: '100px' }}>Start Date:</strong>
                  <span>{new Date(allocation.start_date).toLocaleDateString()}</span>
                </div>
              )}
              {allocation.end_date && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <strong style={{ color: '#153C7F', minWidth: '100px' }}>End Date:</strong>
                  <span>{new Date(allocation.end_date).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Advance Payment Checkbox - Only for start mode */}
        {mode === 'start' && allocation?.advance_amount > 0 && (
          <div style={{
            backgroundColor: '#e8f4fd',
            border: '1px solid #b3d9f2',
            borderRadius: '6px',
            padding: '12px 16px'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              <input
                type="checkbox"
                checked={createAdvancePayment}
                onChange={(e) => setCreateAdvancePayment(e.target.checked)}
                style={{
                  marginRight: '8px',
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span>
                Advance payment of ₹{allocation.advance_amount.toFixed(2)} has been received and create payment record
              </span>
            </label>
          </div>
        )}
      </div>
    </RTModal>
  );
};

export default UpdateAllocationStatusModal;
